import { FC, ReactNode, useCallback, useMemo, useState } from 'react';

import { TippyProps } from '@tippyjs/react';
import classNames from 'classnames';

import { useSafeMobileCheck } from '@dtci/triton/hooks';

import Container, { ContainerProps } from '../../Container';
import Icon from '../../Icon';
import Popover from '../../Popover';
import { TestMetadata } from '../../types';
import { useResponsiveNavBar } from '../NavBarContext';

import './NavBarToggle.scss';

const defaultTippyProps: TippyProps = {
  theme: 'light-border',
  interactiveBorder: 10,
  interactive: true,
  maxWidth: '100vw',
  placement: 'bottom',
};

export interface NavBarToggleProps {
  /** Text to show in the nav bar */
  text?: string;
  /** Can pass in your own menu item to use if not text */
  menuItem?: ReactNode;
  /** Show overlay on either hover (default) or click */
  showChildrenOn?: 'hover' | 'click';
  /** Show arrow next to text menu item or not */
  showArrow?: boolean;
  /** Show arrow above tippy or not */
  showTippyArrow?: boolean;
  /** Should overlay switch to expand menu or overlay in mobile */
  overlayOnMobile?: boolean;
  /** Pass in custom props to tippy */
  tippyProps?: TippyProps;
  /** Custom class to use for menu item wrapper */
  wrapperClassName?: string;
  /** Custom class to use for tooltip */
  className?: string;
  /** Container props to spread to mobile menu */
  containerProps?: Omit<ContainerProps, 'children'>;
  /** Flag to indicate the toggle is not top level in a Nav Bar */
  isNested?: boolean;
  /** TestMetadata ids to use in toggle */
  testMetadata?: {
    container?: TestMetadata;
    mobileMenu?: TestMetadata;
  };
  /** Expand link action. Use in mobile when NavBarToggle has nested links. */
  onExpandChildren?: (item: string) => void;
  /** Control open state */
  isVisible?: boolean;
}

const NavBarToggle: FC<NavBarToggleProps> = ({
  text = 'Toggle',
  menuItem,
  children,
  showChildrenOn = 'hover',
  showArrow = true,
  showTippyArrow = true,
  wrapperClassName,
  className,
  overlayOnMobile = false,
  testMetadata,
  tippyProps,
  isNested = false,
  containerProps = {
    fill: 'white',
    margin: 'centered',
  },
  onExpandChildren,
  isVisible,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const isMobile = useSafeMobileCheck({ initialValue: false });
  const { getBreakpointClassName, useMobileDrawer } = useResponsiveNavBar();

  const sbTippyProps = useMemo(
    () => ({
      visible: isMobile || showChildrenOn === 'click' ? open : false,
      trigger:
        isMobile || showChildrenOn === 'click' ? 'click' : 'mouseenter focus',
      arrow: showTippyArrow,
      offset: [0, 0] as [
        number,
        number,
      ] /** [0, 0] being seen to ts as number[] unless cast */,
      ...defaultTippyProps,
      ...tippyProps,
    }),
    [open, showChildrenOn, showTippyArrow, tippyProps, isMobile],
  );

  const togglableMenuItemClasses = useMemo(
    () =>
      classNames(
        getBreakpointClassName('sb-navbar-toggle'),
        {
          [getBreakpointClassName('sb-navbar-toggle__inline-menu')]:
            !overlayOnMobile,
          [getBreakpointClassName('sb-navbar-toggle__nested')]: isNested,
          [getBreakpointClassName('sb-navbar-toggle__has-menu-item')]: menuItem,
          'sb-navbar-toggle_light': !useMobileDrawer,
        },
        wrapperClassName,
      ),
    [
      getBreakpointClassName,
      overlayOnMobile,
      useMobileDrawer,
      isNested,
      menuItem,
      wrapperClassName,
    ],
  );

  const tooltipClasses = useMemo(
    () =>
      classNames(
        'sb-navbar-toggle__tooltip',
        {
          [getBreakpointClassName('sb-navbar-toggle_hide-on-mobile')]:
            !overlayOnMobile,
        },
        className,
      ),
    [getBreakpointClassName, overlayOnMobile, className],
  );

  const arrowIconClassNames = useMemo(
    () =>
      classNames('sb-navbar-toggle-arrow', {
        [getBreakpointClassName('sb-navbar-toggle-arrow_drawer')]:
          useMobileDrawer,
      }),
    [getBreakpointClassName, useMobileDrawer],
  );

  const arrowIconName = useMemo(() => {
    if (!isMobile) {
      return 'fa-chevron-down';
    }

    if (useMobileDrawer) {
      return 'fa-chevron-right';
    }

    return open ? 'fa-chevron-up' : 'fa-chevron-down';
  }, [isMobile, open, useMobileDrawer]);

  const handleMenuClick = useCallback((): void => {
    if (isMobile && !useMobileDrawer) {
      setOpen(!open);

      return;
    }

    onExpandChildren?.(text);
  }, [isMobile, useMobileDrawer, onExpandChildren, text, open]);

  return (
    <>
      <Popover
        tippyProps={sbTippyProps}
        className={tooltipClasses}
        popoverContent={<span>{children}</span>}
        visible={isVisible}
      >
        <div
          className={togglableMenuItemClasses}
          onClick={handleMenuClick}
          {...testMetadata?.container}
        >
          {menuItem ?? text}
          {showArrow && (
            <Icon
              altText="Toggle"
              iconName={arrowIconName}
              faPackage="fas"
              iconType="fa"
              size="smaller"
              title="Toggle"
              className={arrowIconClassNames}
            />
          )}
        </div>
      </Popover>

      {!overlayOnMobile && open && (
        <Container
          className={classNames(
            `${getBreakpointClassName(
              'sb-navbar-toggle__mobile',
            )} ${getBreakpointClassName('sb-navbar-toggle_show-on-mobile')}`,
          )}
          {...containerProps}
          {...testMetadata?.mobileMenu}
        >
          {children}
        </Container>
      )}
    </>
  );
};

export default NavBarToggle;
